import { queryOptions } from "@tanstack/react-query";
import { analysisClient } from "@/shared/lib/supabase";
import { getKeyOnlyQueryConfig } from "@/shared/lib/key-only-query-config";
import { getUserTenantId } from "@/domains/tenant/tenantId";
import { fetchRecordOwners } from "@/domains/record-owners";
import { EXTERNAL_RESOURCE_CONTENT_TYPE } from "@/domains/resources";
import { parseMatrixSettings } from "@/domains/matrix";
import type { RecordOwner } from "@/domains/record-owners";
import type {
  AnalysisRow,
  TemplateRow,
  TemplateSettingRow,
  TenantCollectionRow,
  Json,
} from "@/types/supabase.types";

type DashboardReportRow = AnalysisRow & {
  analysis_setting: {
    display_name: string | null;
  };
};

type DashboardTemplateRow = TemplateRow & {
  template_setting: Pick<TemplateSettingRow, "display_name"> | null;
};

type DashboardMatrixRow = Pick<
  TenantCollectionRow,
  | "tenant_collection_id"
  | "tenant_collection_name"
  | "record_updated"
  | "record_creator"
> & {
  tenant_collection_setting: { settings: Json | null } | null;
  analysis: { count: number }[];
};

export type DashboardMatrixItem = {
  id: string;
  name: string;
  rowCount: number;
  columnCount: number;
  updatedAt: string;
  owner: RecordOwner;
};

type DashboardResourceRow = {
  container: string;
  content_type: string;
  media_extension: string;
  media_id: string;
  media_name: string;
  object_key: string;
  record_created: string;
  record_creator: string;
  record_updated: string;
};

export type DashboardReportItem = {
  id: string;
  name: string;
  type: string;
  updatedAt: string;
  status: AnalysisRow["analysis_status"];
  owner: RecordOwner;
};

export type DashboardTemplateItem = {
  id: string;
  name: string;
  type: string;
  updatedAt: string;
  owner: RecordOwner;
};

export type DashboardResourceItem = {
  id: string;
  name: string;
  type: string;
  updatedAt: string;
  owner: RecordOwner;
  container: string;
  objectKey: string;
};

export type DashboardData = {
  reportCount: number;
  matrixCount: number;
  templateCount: number;
  resourceCount: number;
  reports: DashboardReportItem[];
  matrices: DashboardMatrixItem[];
  templates: DashboardTemplateItem[];
  resources: DashboardResourceItem[];
};

function formatResourceType(row: DashboardResourceRow) {
  const extension = row.media_extension.trim();
  if (extension.length > 0) return extension.toUpperCase();

  const contentType = row.content_type.split("/").at(-1)?.trim() ?? "";
  return contentType.toUpperCase() || "FILE";
}

async function fetchStandardCollectionId(tenantId: string) {
  const { data, error } = await analysisClient
    .from("tenant_collection")
    .select("tenant_collection_id")
    .eq("tenant_id", tenantId)
    .eq("tenant_collection_name", "Standard")
    .maybeSingle();

  if (error) throw error;
  return data?.tenant_collection_id ?? null;
}

async function fetchDashboardMatrixRows(
  tenantId: string,
): Promise<DashboardMatrixRow[]> {
  const rows: DashboardMatrixRow[] = [];
  const pageSize = 1000;

  for (let offset = 0; ; offset += pageSize) {
    const { data, error } = await analysisClient
      .from("tenant_collection")
      .select(
        "tenant_collection_id, tenant_collection_name, record_updated, record_creator, tenant_collection_setting(settings), analysis(count)",
      )
      .eq("tenant_id", tenantId)
      .eq("analysis.analysis_type", "matrix")
      .order("tenant_collection_id", { ascending: true })
      .range(offset, offset + pageSize - 1);

    if (error) throw error;
    const page = data ?? [];
    rows.push(...page);
    if (page.length < pageSize) return rows;
  }
}

async function fetchDashboardData(): Promise<DashboardData> {
  const userTenantId = await getUserTenantId();
  const standardCollectionId = await fetchStandardCollectionId(userTenantId);

  const reportCountQuery = analysisClient
    .from("analysis")
    .select("analysis_id", { count: "exact", head: true })
    .eq("record_tenant_id", userTenantId);
  // One OR query counts each report once, including Matrix rows.
  const reportCountPromise = standardCollectionId
    ? reportCountQuery.or(
        `tenant_collection_id.eq.${standardCollectionId},analysis_type.eq.matrix`,
      )
    : reportCountQuery.eq("analysis_type", "matrix");

  const matricesPromise = fetchDashboardMatrixRows(userTenantId);

  const reportsPromise = standardCollectionId
    ? analysisClient
        .from("analysis")
        .select("*, analysis_setting!inner(display_name)")
        .eq("tenant_collection_id", standardCollectionId)
        .eq("record_tenant_id", userTenantId)
        .order("record_updated", { ascending: false })
        .limit(5)
    : Promise.resolve({ data: [], error: null });

  const templateCountPromise = analysisClient
    .from("template")
    .select("analysis_id", { count: "exact", head: true })
    .eq("record_tenant_id", userTenantId)
    .eq("analysis_type", "custom");

  const templatesPromise = analysisClient
    .from("template")
    .select("*, template_setting(display_name)")
    .eq("record_tenant_id", userTenantId)
    .eq("analysis_type", "custom")
    .order("record_updated", { ascending: false })
    .limit(5);

  const resourceCountPromise = analysisClient
    .from("media")
    .select("media_id", { count: "exact", head: true })
    .eq("record_tenant_id", userTenantId)
    .neq("content_type", EXTERNAL_RESOURCE_CONTENT_TYPE);

  const resourcesPromise = analysisClient
    .from("media")
    .select(
      "container, content_type, media_extension, media_id, media_name, object_key, record_created, record_creator, record_updated",
    )
    .eq("record_tenant_id", userTenantId)
    .neq("content_type", EXTERNAL_RESOURCE_CONTENT_TYPE)
    .order("record_updated", { ascending: false })
    .limit(5);

  const [
    reportCountResult,
    reportsResult,
    templateCountResult,
    templatesResult,
    resourceCountResult,
    resourcesResult,
    matricesResult,
  ] = await Promise.all([
    reportCountPromise,
    reportsPromise,
    templateCountPromise,
    templatesPromise,
    resourceCountPromise,
    resourcesPromise,
    matricesPromise,
  ]);

  if (reportCountResult.error) throw reportCountResult.error;
  if (reportsResult.error) throw reportsResult.error;
  if (templateCountResult.error) throw templateCountResult.error;
  if (templatesResult.error) throw templatesResult.error;
  if (resourceCountResult.error) throw resourceCountResult.error;
  if (resourcesResult.error) throw resourcesResult.error;

  const reportRows = (reportsResult.data ?? []) as DashboardReportRow[];
  const templateRows = (templatesResult.data ?? []) as DashboardTemplateRow[];
  const resourceRows = (resourcesResult.data ?? []) as DashboardResourceRow[];
  const matrixRows = matricesResult
    .flatMap((row) => {
      const settings = parseMatrixSettings(
        row.tenant_collection_setting?.settings,
      );
      return settings ? [{ row, columnCount: settings.columns.length }] : [];
    })
    .sort((a, b) => b.row.record_updated.localeCompare(a.row.record_updated));
  const recentMatrices = matrixRows.slice(0, 5);

  const owners = await fetchRecordOwners([
    ...reportRows.map((row) => row.record_creator),
    ...templateRows
      .map((row) => row.record_creator)
      .filter((value): value is string => Boolean(value)),
    ...resourceRows.map((row) => row.record_creator),
    ...recentMatrices.map(({ row }) => row.record_creator),
  ]);

  return {
    reportCount: reportCountResult.count ?? 0,
    matrixCount: matrixRows.length,
    matrices: recentMatrices.map(({ row, columnCount }) => ({
      id: row.tenant_collection_id,
      name: row.tenant_collection_name,
      rowCount: row.analysis[0]?.count ?? 0,
      columnCount,
      updatedAt: row.record_updated,
      owner: owners[row.record_creator] ?? {
        name: "Unknown user",
        initials: "??",
      },
    })),
    templateCount: templateCountResult.count ?? 0,
    resourceCount: resourceCountResult.count ?? 0,
    reports: reportRows.map((row) => ({
      id: row.analysis_id,
      name: row.analysis_setting.display_name?.trim() || row.analysis_name,
      type: row.analysis_type,
      updatedAt: row.record_updated,
      status: row.analysis_status,
      owner: owners[row.record_creator] ?? {
        name: "Unknown user",
        initials: "??",
      },
    })),
    templates: templateRows.map((row) => ({
      id: row.analysis_id ?? "",
      name:
        row.template_setting?.display_name?.trim() ||
        row.analysis_name ||
        "Untitled template",
      type: row.analysis_type ?? "",
      updatedAt: row.record_updated ?? "",
      owner: (row.record_creator ? owners[row.record_creator] : undefined) ?? {
        name: "Researchly",
        initials: "RE",
      },
    })),
    resources: resourceRows.map((row) => ({
      id: row.media_id,
      name: row.media_name,
      type: formatResourceType(row),
      updatedAt: row.record_updated,
      owner: owners[row.record_creator] ?? {
        name: "Unknown user",
        initials: "??",
      },
      container: row.container,
      objectKey: row.object_key,
    })),
  };
}

const dashboardBase = getKeyOnlyQueryConfig(["dashboard"]);

export const dashboardQueries = {
  all: dashboardBase,
  data: () =>
    queryOptions({
      queryKey: [...dashboardBase.queryKey, "data"],
      queryFn: fetchDashboardData,
    }),
};
